import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAccessContext } from "@/hooks/useAccessContext";
import { useMfaRequirement } from "@/hooks/useMfaRequirement";
import { createSessionVerificationTimeoutError, useLoadingTimeout } from "@/hooks/useLoadingTimeout";
import { ForgePageLoader } from "@/components/ForgeLoader";
import {
  logAuthTransition,
  trackRoute,
} from "@/lib/auth-transition-logger";
import {
  getAuthRedirectReason,
  getAuthRedirectTarget,
  hasExplicitSignedOutState,
  useInitialSessionErrorRecovery,
} from "@/components/layout/routeGuardUtils";

interface AdminRouteProps {
  children: React.ReactNode;
}

const ADMIN_ROUTE_GUARD_LOADING_TIMEOUT_MS = 45_000;

/**
 * Route guard for Forge HQ platform administrators only.
 * Requires the user to have a platform role.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const {
    isAuthenticated,
    isLoading: authLoading,
    hasSessionError,
    authError,
    lastAuthEvent,
    rehydrateSession,
  } = useAuth();
  const { data: accessContext, isLoading: accessLoading } = useAccessContext();
  const isPlatformAdmin = accessContext?.capabilities.isPlatformAdmin ?? false;
  const location = useLocation();

  const { status: mfaStatus, needsChallenge, needsSetup, requiresMfa, reauthRequired } = useMfaRequirement();
  const mfaReady = mfaStatus === "checked";
  const isRecoveringInitialSession = useInitialSessionErrorRecovery({
    isAuthenticated,
    isAuthLoading: authLoading,
    hasSessionError,
    lastAuthEvent,
    rehydrateSession,
  });

  const currentRoute = `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;
  const isLoading = isAuthenticated && (accessLoading || !mfaReady);
  const isLoadingTimedOut = useLoadingTimeout(isLoading, ADMIN_ROUTE_GUARD_LOADING_TIMEOUT_MS);

  // Track route changes for auth transition logging
  useEffect(() => {
    trackRoute(currentRoute);
  }, [currentRoute]);

  // STEP 1: Wait for auth check to complete.
  if (authLoading || isRecoveringInitialSession) {
    return <ForgePageLoader />;
  }

  const hasConfirmedSignedOutState = hasExplicitSignedOutState(
    isAuthenticated,
    lastAuthEvent,
  );

  // STEP 2 (auth resolved): Throw session errors to boundary.
  if (hasSessionError && !isAuthenticated && !hasConfirmedSignedOutState) {
    const errorMessage = authError?.message || "Session check failed";
    const error = new Error(`Session verification failed: ${errorMessage}`);
    error.name = "SessionVerificationError";
    throw error;
  }

  // STEP 3 (auth resolved): Redirect unauthenticated users immediately.
  if (!isAuthenticated) {
    const redirectReason = getAuthRedirectReason(lastAuthEvent);

    logAuthTransition(redirectReason, {
      lastAuthEvent,
      fromRoute: currentRoute,
      routeGuard: "AdminRoute",
    });

    const authTarget = getAuthRedirectTarget(location);
    return <Navigate to={authTarget} state={{ from: location }} replace />;
  }

  // STEP 4: User is authenticated — now wait for roles and MFA.
  if (isLoading && isLoadingTimedOut) {
    throw createSessionVerificationTimeoutError(
      "Session verification is taking longer than expected while loading administrator permissions and MFA status.",
    );
  }

  if (isLoading) {
    return <ForgePageLoader />;
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/403" replace />;
  }

  if (requiresMfa && mfaReady) {
    if (needsSetup && location.pathname !== "/mfa/setup") {
      return <Navigate to="/mfa/setup" state={{ from: location }} replace />;
    }

    if (needsChallenge && location.pathname !== "/mfa/challenge") {
      const challengePath = reauthRequired ? "/mfa/challenge?reauth=1" : "/mfa/challenge";
      return <Navigate to={challengePath} state={{ from: location }} replace />;
    }
  }

  return <>{children}</>;
}
